/** XeAgents.ai**/
import { Switch, Route, useLocation, Redirect, Router as BaseRouter } from "wouter";
import { BASE_PATH, withBase } from "@/lib/base-path";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { Zap } from "lucide-react";
import { AppSidebar } from "@/components/app-sidebar";
import { ThemeProvider } from "@/components/ThemeProvider";
import { BrandingProvider, useBranding } from "@/components/BrandingProvider";
import { TenantProvider } from "@/components/TenantProvider";
import { ProductProvider, useProduct } from "@/components/ProductProvider";
import { DirectionProvider } from "@/components/DirectionProvider";
import { ThemeToggle } from "@/components/ThemeToggle";
import { LanguageSelector } from "@/components/LanguageSelector";
import { useEffect, useState } from "react";
import { AuthStorage } from "./lib/auth-storage";
import Dashboard from "@/pages/Dashboard";
import Campaigns from "@/pages/Campaigns";
import CampaignDetail from "@/pages/CampaignDetail";
import AiB2bLeadPage from "@/pages/AiB2bLeadPage";
import LeadsSearchPage from "@/pages/LeadsSearchPage";
import Calls from "@/pages/Calls";
import CallDetail from "@/pages/CallDetail";
import Leads from "@/pages/Leads";
import Analytics from "@/pages/Analytics";
import Billing from "@/pages/Billing";
import OrganizationOverview from "@/pages/organization/OrganizationOverview";
import TeamMembers from "@/pages/organization/TeamMembers";
import PendingInvitations from "@/pages/organization/PendingInvitations";
import BillingAndSeats from "@/pages/organization/BillingAndSeats";
import IntegrationsSettings from "@/pages/organization/IntegrationsSettings";
import AcceptInvitation from "@/pages/AcceptInvitation";
import PaymentResult from "@/pages/PaymentResult";
import Upgrade from "@/pages/Upgrade";
import KnowledgeBase from "@/pages/KnowledgeBase";
import Agents from "@/pages/Agents";
import Healthcare from "@/pages/Healthcare";
import Voices from "@/pages/Voices";
import PhoneNumbers from "@/pages/PhoneNumbers";
import AllContacts from "@/pages/AllContacts";
import Settings from "@/pages/Settings";
import Login from "@/pages/Login";
import SignIn from "@/pages/SignIn";
import LandingPage from "@/pages/LandingPage";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminCampaignDetail from "@/pages/AdminCampaignDetail";
import SEODashboard from "@/pages/SEODashboard";
import PrivacyPolicy from "@/pages/policies/PrivacyPolicy";
import TermsOfService from "@/pages/policies/TermsOfService";
import CookiePolicy from "@/pages/policies/CookiePolicy";
import InstallWizard from "@/pages/InstallWizard";
import NotFound from "@/pages/not-found";
import FlowsPage from "@/pages/FlowsPage";
import FlowBuilderPage from "@/pages/FlowBuilderPage";
import AIFlowCreatePage from "@/pages/AIFlowCreatePage";
import FlowExecutionLogsPage from "@/pages/FlowExecutionLogsPage";
import WebhookConfigPage from "@/pages/WebhookConfigPage";
import FormsPage from "@/pages/FormsPage";
import AppointmentsPage from "@/pages/AppointmentsPage";
import FlowTemplatesPage from "@/pages/FlowTemplatesPage";
import FlowTemplateCustomizePage from "@/pages/FlowTemplateCustomizePage";
import IncomingConnections from "@/pages/IncomingConnections";
import CRM from "@/pages/CRM";
import PromptTemplates from "@/pages/PromptTemplates";
import TransactionHistory from "@/pages/TransactionHistory";
import FollowUpQueue from "@/pages/FollowUpQueue";
// Recruitment page removed - only available in xehirepro
import XeHireProLanding from "@/pages/XeHireProLanding";
import HelpPage from "@/pages/HelpPage";
import HelpDeskPage from "@/pages/HelpDeskPage";
import TicketDetailPage from "@/pages/TicketDetailPage";
import AdminHelpDeskPage from "@/pages/AdminHelpDeskPage";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Contact from "@/pages/Contact";
import CalendarConnectPage from "@/pages/CalendarConnectPage";
// Custom module pages (for enterprise tenants)
import VendingSupport from "@/pages/custom-modules/VendingSupport";
import InventoryAlerts from "@/pages/custom-modules/InventoryAlerts";
import { ScrollToTop } from "@/components/ScrollToTop";
import { NotificationBell } from "@/components/NotificationBell";
import { HeaderBannerNotifications } from "@/components/HeaderBannerNotifications";
import { CookieConsentBanner } from "@/components/CookieConsentBanner";
import { AnalyticsScripts } from "@/components/AnalyticsScripts";
import { AnalyticsAutoInject } from "@/components/AnalyticsAutoInject";
import { PlanSelectionModal } from "@/components/PlanSelectionModal";

function LoginRedirect() {
  const [, setLocation] = useLocation();
  useEffect(() => {
    setLocation("/?auth=login");
  }, [setLocation]);
  return null;
}

function RegisterRedirect() {
  const [, setLocation] = useLocation();
  useEffect(() => {
    setLocation("/?auth=register");
  }, [setLocation]);
  return null;
}

function ProductLandingPage() {
  const { isXeHirePro } = useProduct();
  return isXeHirePro ? <XeHireProLanding /> : <LandingPage />;
}

function PublicRouter() {
  return (
    <>
      <ScrollToTop />
      <Switch>
        <Route path="/" component={ProductLandingPage} />
        <Route path="/signin" component={SignIn} />
        <Route path="/login" component={LoginRedirect} />
        <Route path="/register" component={RegisterRedirect} />
        <Route path="/contact" component={Contact} />
        <Route path="/blog/:slug" component={BlogPost} />
        <Route path="/blog" component={Blog} />
        <Route path="/privacy" component={PrivacyPolicy} />
        <Route path="/terms" component={TermsOfService} />
        <Route path="/cookies" component={CookiePolicy} />
        <Route path="/invitation/:token" component={AcceptInvitation} />
        <Route path="/calendar-connect" component={CalendarConnectPage} />
        {/* Payment result needs to work without auth (for Stripe redirects after checkout) */}
        <Route path="/app/payment-result" component={PaymentResult} />
        <Route component={ProductLandingPage} />
      </Switch>
    </>
  );
}

function AdminRouter() {
  const style = {
    "--sidebar-width": "14rem",
    "--sidebar-width-icon": "3rem",
  };
  const { branding, currentLogo } = useBranding();

  return (
    <SidebarProvider style={style as React.CSSProperties} defaultOpen={typeof window === "undefined" || window.innerWidth >= 1280}>
      <div className="flex h-screen w-full">
        <AppSidebar />
        <div className="flex flex-col flex-1 overflow-hidden">
          <header className="flex items-center justify-between h-14 px-4 md:px-6 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <div className="flex items-center gap-3">
              <SidebarTrigger
                className="md:hidden"
                data-testid="button-mobile-menu"
              />
              <div className="md:hidden">
                {currentLogo ? (
                  <img
                    src={currentLogo}
                    alt={branding.app_name}
                    className="h-7 w-auto max-w-[120px] object-contain"
                  />
                ) : (
                  <Zap className="h-6 w-6 text-primary" />
                )}
              </div>
            </div>
            <div className="flex-1" />
            <div className="flex items-center gap-2">
              <HeaderBannerNotifications />
              <NotificationBell />
              <LanguageSelector variant="compact" />
              <ThemeToggle />
            </div>
          </header>
          <main className="flex-1 overflow-auto">
            <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
              <Switch>
                <Route path="/">
                  <Redirect to="/admin" />
                </Route>
                <Route path="/admin" component={AdminDashboard} />
                <Route path="/admin/dashboard" component={AdminDashboard} />
                <Route path="/admin/helpdesk" component={AdminHelpDeskPage} />
                <Route path="/admin/seo" component={SEODashboard} />
                <Route
                  path="/admin/campaigns/:id"
                  component={AdminCampaignDetail}
                />
                <Route component={NotFound} />
              </Switch>
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}

function UserRouter() {
  const style = {
    "--sidebar-width": "14rem",
    "--sidebar-width-icon": "3rem",
  };
  const { branding, currentLogo } = useBranding();

  return (
    <SidebarProvider style={style as React.CSSProperties} defaultOpen={typeof window === "undefined" || window.innerWidth >= 1280}>
      <div className="flex h-screen w-full">
        <AppSidebar />
        <div className="flex flex-col flex-1 overflow-hidden">
          <header className="flex items-center justify-between h-14 px-4 md:px-6 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <div className="flex items-center gap-3">
              <SidebarTrigger
                className="md:hidden"
                data-testid="button-mobile-menu"
              />
              <div className="md:hidden">
                {currentLogo ? (
                  <img
                    src={currentLogo}
                    alt={branding.app_name}
                    className="h-7 w-auto max-w-[120px] object-contain"
                  />
                ) : (
                  <Zap className="h-6 w-6 text-primary" />
                )}
              </div>
            </div>
            <div className="flex-1" />
            <div className="flex items-center gap-2">
              <HeaderBannerNotifications />
              <NotificationBell />
              <LanguageSelector variant="compact" />
              <ThemeToggle />
            </div>
          </header>
          <main className="flex-1 overflow-auto">
            <div className="w-full px-4 md:px-10 lg:px-16 xl:px-20 py-4 md:py-6">
              <Switch>
                <Route path="/">
                  <Redirect to="/app" />
                </Route>
                <Route path="/app" component={Dashboard} />
                <Route path="/app/dashboard" component={Dashboard} />
                <Route path="/app/smart-prospecting" component={AiB2bLeadPage} />
                <Route path="/app/leads-search" component={LeadsSearchPage} />
                {/* Both lead sources are tabs of AI B2B Lead now. The section was Lead Lists, and LinkedIn Leads
                    before that; keep both sets of bookmarks landing on the right tab. */}
                <Route path="/app/ai-b2b-leads">
                  <Redirect to="/app/smart-prospecting?tab=leadscm" />
                </Route>
                <Route path="/app/linkedin-leads">
                  <Redirect to="/app/smart-prospecting?tab=leadscm" />
                </Route>
                <Route path="/app/campaigns/:id" component={CampaignDetail} />
                <Route path="/app/campaigns" component={Campaigns} />
                <Route path="/app/follow-up-queue" component={FollowUpQueue} />
                <Route path="/app/healthcare" component={Healthcare} />
                <Route path="/app/calls/:id" component={CallDetail} />
                <Route path="/app/calls" component={Calls} />
                <Route path="/app/leads" component={Leads} />
                <Route path="/app/contacts" component={AllContacts} />
                <Route path="/app/analytics" component={Analytics} />
                <Route path="/app/billing" component={Billing} />
                <Route path="/app/payment-result" component={PaymentResult} />
                <Route
                  path="/app/transaction-history"
                  component={TransactionHistory}
                />
                <Route path="/app/upgrade" component={Upgrade} />
                <Route path="/app/knowledge-base" component={KnowledgeBase} />
                <Route path="/app/agents" component={Agents} />
                <Route
                  path="/app/prompt-templates"
                  component={PromptTemplates}
                />
                <Route
                  path="/app/incoming-connections"
                  component={IncomingConnections}
                />
                <Route path="/app/crm" component={CRM} />
                <Route path="/app/voices" component={Voices} />
                <Route path="/app/phone-numbers" component={PhoneNumbers} />
                <Route
                  path="/app/tools"
                  component={() => (
                    <div className="text-center py-16 text-muted-foreground">
                      Tools page coming soon
                    </div>
                  )}
                />
                <Route path="/app/flows/new" component={FlowBuilderPage} />
                <Route
                  path="/app/flows/execution"
                  component={FlowExecutionLogsPage}
                />
                <Route
                  path="/app/flows/webhooks"
                  component={WebhookConfigPage}
                />
                <Route path="/app/flows/forms" component={FormsPage} />
                <Route
                  path="/app/flows/appointments"
                  component={AppointmentsPage}
                />
                <Route
                  path="/app/flows/templates/:templateId/customize"
                  component={FlowTemplateCustomizePage}
                />
                <Route
                  path="/app/flows/templates"
                  component={FlowTemplatesPage}
                />
                <Route
                  path="/app/flows/ai-create"
                  component={AIFlowCreatePage}
                />
                <Route
                  path="/app/flows/:id/customize"
                  component={FlowTemplateCustomizePage}
                />
                <Route path="/app/flows/:id" component={FlowBuilderPage} />
                <Route path="/app/flows" component={FlowsPage} />
                <Route
                  path="/app/outbound"
                  component={() => (
                    <div className="text-center py-16 text-muted-foreground">
                      Outbound page coming soon
                    </div>
                  )}
                />
                <Route path="/app/organization/members" component={TeamMembers} />
                <Route path="/app/organization/invitations" component={PendingInvitations} />
                <Route path="/app/organization/billing" component={BillingAndSeats} />
                <Route path="/app/organization/integrations" component={IntegrationsSettings} />
                <Route path="/app/organization" component={OrganizationOverview} />
                <Route path="/app/settings" component={Settings} />
                <Route path="/app/helpdesk/:id" component={TicketDetailPage} />
                <Route path="/app/helpdesk" component={HelpDeskPage} />
                <Route path="/app/help" component={HelpPage} />
                <Route
                  path="/app/developers"
                  component={() => (
                    <div className="text-center py-16 text-muted-foreground">
                      Developers page coming soon
                    </div>
                  )}
                />
                {/* Custom Module Routes (for enterprise tenants like Elite Vending) */}
                <Route path="/app/vending" component={VendingSupport} />
                <Route path="/app/inventory" component={InventoryAlerts} />
                <Route component={NotFound} />
              </Switch>
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}

interface User {
  id: string;
  name: string;
  email: string;
  isSuperAdmin: boolean;
  role?: string;
  credits?: number;
  mustChangePassword?: boolean;
  planType?: string;
}

interface OrgData {
  organization: { id: string; name: string };
  role: string | null;
}

function AdminGuard({ children }: { children: React.ReactNode }) {
  // Fetch user from server to validate admin access
  const {
    data: user,
    isLoading: userLoading,
    isError,
  } = useQuery<User>({
    queryKey: ["/api/auth/me"],
    retry: false,
  });

  // Handle auth errors
  useEffect(() => {
    if (isError) {
      AuthStorage.clearAuth();
      window.location.href = withBase("/login");
    }
  }, [isError]);

  // Show loading while fetching user data
  if (userLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  // If no user data at all (shouldn't happen after loading), redirect to login
  if (!user) {
    console.log("AdminGuard - No user data, redirecting to /login");
    return <Redirect to="/login" />;
  }

  const isSuperAdmin = user.isSuperAdmin === true;
  const hasAdminAccess = isSuperAdmin;

  // If user doesn't have admin access, redirect to user panel
  if (!hasAdminAccess) {
    console.log(
      "AdminGuard - User is not authorized for admin panel, redirecting to /app"
    );
    return <Redirect to="/app" />;
  }

  console.log("AdminGuard - User has admin access, rendering admin panel");
  return <>{children}</>;
}

function UserGuard({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();

  // Fetch user from server
  const {
    data: user,
    isLoading,
    isError,
  } = useQuery<User>({
    queryKey: ["/api/auth/me"],
    retry: false,
  });

  // Fetch org role to gate the credits modal (cached by React Query, no extra network request)
  const { data: orgData } = useQuery<OrgData>({
    queryKey: ["/api/organizations/me"],
    enabled: !!user,
    retry: false,
  });

  const [showPlanSelection, setShowPlanSelection] = useState(false);
  const [showEnterprisePlans, setShowEnterprisePlans] = useState(false);

  // Handle auth errors
  useEffect(() => {
    if (isError) {
      AuthStorage.clearAuth();
      window.location.href = withBase("/login");
    }
  }, [isError]);

  // Check if enterprise user should see plans on first login
  useEffect(() => {
    if (user && user.planType === 'enterprise') {
      // Check if user just changed password (redirected from settings)
      const urlParams = new URLSearchParams(window.location.search);
      const passwordChanged = urlParams.get('passwordChanged') === 'true';

      // Check if they've already seen the plans modal
      const hasSeenPlans = localStorage.getItem(`enterprise_plans_seen_${user.id}`);

      if (passwordChanged && !hasSeenPlans) {
        setShowEnterprisePlans(true);
        // Clean up URL
        window.history.replaceState({}, '', location);
      }
    }
  }, [user, location]);

  // Check if user has run out of credits — only show modal to org owners
  useEffect(() => {
    // Allow access to /app/upgrade without blocking (for payment flow)
    if (location === "/app/upgrade" || location === "/app/payment-result") {
      setShowPlanSelection(false);
      return;
    }

    if (user) {
      const userCredits = user.credits ?? 0;
      // Only org owners are responsible for billing; team members use owner's account
      const isOrgOwner = orgData?.role === "owner";
      if (userCredits <= 0 && isOrgOwner) {
        setShowPlanSelection(true);
      } else {
        setShowPlanSelection(false);
      }
    }
  }, [user, orgData, location]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    return <Redirect to="/login" />;
  }

  // Force password change before accessing any other page
  if (user.mustChangePassword && location !== "/app/settings") {
    return <Redirect to="/app/settings?requirePasswordChange=true" />;
  }

  // Show enterprise welcome plans modal on first login
  if (showEnterprisePlans && user) {
    return (
      <>
        {children}
        <PlanSelectionModal
          isOpen={true}
          mode="welcome"
          title="Welcome to Your Enterprise Account! 🎉"
          description="Here are all available plans for reference. Your enterprise account is already active with custom pricing!"
          onPlanSelected={() => {
            // Mark as seen and close
            localStorage.setItem(`enterprise_plans_seen_${user.id}`, 'true');
            setShowEnterprisePlans(false);
          }}
          onClose={() => {
            // Mark as seen and close
            localStorage.setItem(`enterprise_plans_seen_${user.id}`, 'true');
            setShowEnterprisePlans(false);
          }}
        />
      </>
    );
  }

  // Show plan selection modal only when credits reach zero (except on upgrade/payment pages)
  if (showPlanSelection) {
    return (
      <PlanSelectionModal
        isOpen={true}
        onPlanSelected={() => {
          // Refresh page after plan selection
          window.location.reload();
        }}
      />
    );
  }

  return <>{children}</>;
}

function Router() {
  const [location] = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  // Check installation status
  const { data: installStatus } = useQuery<{ installed: boolean }>({
    queryKey: ["/api/installer/status"],
    retry: false,
  });

  // Check for auth token using centralized storage
  useEffect(() => {
    setIsAuthenticated(AuthStorage.isAuthenticated());
    setIsChecking(false);
  }, []);

  // Debug logging
  useEffect(() => {
    console.log("Router - Current location:", location);
    console.log("Router - isAuthenticated:", isAuthenticated);
    console.log(
      "Router - location.startsWith('/admin'):",
      location.startsWith("/admin")
    );
    console.log(
      "Router - location.startsWith('/app'):",
      location.startsWith("/app")
    );
  }, [location, isAuthenticated]);

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  // Define public routes that should be accessible even if not installed
  const publicRoutes = [
    "/",
    "/login",
    "/register",
    "/contact",
    "/privacy",
    "/terms",
    "/cookies",
    "/install",
    "/calendar-connect",
  ];
  const isPublicRoute =
    publicRoutes.includes(location) || location.startsWith("/blog");

  // Block installer if already installed
  if (location === "/install" && installStatus?.installed) {
    console.log("Router - Already installed, redirecting to /login");
    return <Redirect to="/login" />;
  }

  // Installer route (public, no auth required)
  if (location === "/install") {
    console.log("Router - Rendering InstallWizard");
    return <InstallWizard />;
  }

  // Installation check - if not installed, redirect to installer unless on a public route
  if (installStatus && !installStatus.installed && !isPublicRoute) {
    console.log("Router - Not installed, redirecting to /install");
    return <Redirect to="/install" />;
  }

  // Public routes (landing, login, register)
  if (!isAuthenticated) {
    console.log("Router - Rendering PublicRouter");
    return <PublicRouter />;
  }

  // Admin routes - protected by server-side validation
  if (location.startsWith("/admin")) {
    console.log("Router - Rendering AdminGuard + AdminRouter");
    return (
      <AdminGuard>
        <AdminRouter />
      </AdminGuard>
    );
  }

  // User routes - protected by server-side validation
  console.log("Router - Rendering UserGuard + UserRouter");
  return (
    <UserGuard>
      <UserRouter />
    </UserGuard>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ProductProvider>
        <ThemeProvider>
          <DirectionProvider>
            <BrandingProvider>
              <TenantProvider>
                <TooltipProvider>
                  {/* Served under a region prefix such as /eu: routes, links and navigation all gain it */}
                  <BaseRouter base={BASE_PATH}>
                    <AnalyticsAutoInject />
                    <AnalyticsScripts />
                    <Router />
                    <Toaster />
                    <CookieConsentBanner />
                  </BaseRouter>
                </TooltipProvider>
              </TenantProvider>
            </BrandingProvider>
          </DirectionProvider>
        </ThemeProvider>
      </ProductProvider>
    </QueryClientProvider>
  );
}
